import { Badge, Skeleton, Stack, Text } from '@chakra-ui/react';
import {
  ActionMeta,
  AsyncCreatableSelect,
  ChakraStylesConfig,
  MultiValue,
} from 'chakra-react-select';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  SubscriptionInfosFragment,
  useAttachSubscriptionTagMutation,
  useCreateAndAttachTagMutation,
  useDetachSubscriptionTagMutation,
  useGetSubscriptionQuery,
  useGetTagsQuery,
} from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard, useErrorToast } from '@blockpulse3/ui/commons';

import { TagInput, TagMultiValue, TagPlaceholder } from './components';
import { TagOption } from './types';
import { useGetNextColorTag } from './useGetNextColorTag';

type Props = {
  subscriptionId: SubscriptionInfosFragment['id'];
};

const chakraStyles: ChakraStylesConfig<TagOption, true> = {
  placeholder: (provided) => ({
    ...provided,
    color: 'gray.400',
  }),
};

/*
 * SubscriptionDetailsTags.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriptionDetailsTags({ subscriptionId }: Props): JSX.Element {
  const i18nSubscriptionDetailsTags = useTranslations('SubscriptionDetailsTags');

  const { companyId = '', fundraisingId = '' } = useParams();

  const errorToast = useErrorToast();

  const { data, loading, error, refetch } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });

  const [attachTag] = useAttachSubscriptionTagMutation();
  const [detachTag] = useDetachSubscriptionTagMutation();
  const [createAndAttachTag] = useCreateAndAttachTagMutation();

  /* ** AsyncCreatableSelect key, used for the reloading ** */
  const [selectKey, setSelectKey] = useState<string>('select-id');

  const { data: tagsData, refetch: refetchTags } = useGetTagsQuery({
    variables: { getTagsInput: { companyId, fundraisingId } },
    skip: !companyId || !fundraisingId,
  });

  /* ** Hold next tag color ** */
  const createTagColor = useGetNextColorTag({ companyId, fundraisingId });

  /* ** Promise callback to load tags on input change ** */
  const loadOptions = useCallback(
    async (inputValue: string) => {
      const companyTags = tagsData?.getTags || [];
      return companyTags
        .filter((tag) => tag.name.toLowerCase().includes(inputValue.toLowerCase()))
        .map((tag) => ({ label: tag.name, value: tag.id, color: tag.color }));
    },
    [tagsData],
  );

  if (error) {
    return <ErrorQueryCard h="60px" mx="3" />;
  }

  const handleTagCreate = (name: string): void => {
    createAndAttachTag({
      variables: {
        createAndAttachTagInput: {
          companyId,
          name,
          color: createTagColor,
          subscriptionId,
        },
      },
      onCompleted: (data) => {
        refetch();
        refetchTags();
        /* ** Reload `<AsyncCreatableSelect />` loadOptions by updating the key ** */
        setSelectKey(data.createAndAttachTag.id);
      },
      onError: () => {
        errorToast({ title: i18nSubscriptionDetailsTags('Toasts.CreateError') });
      },
    });
  };

  const handleTagChange = (_: MultiValue<TagOption>, action: ActionMeta<TagOption>): void => {
    switch (action.action) {
      case 'select-option': {
        if (action.option) {
          attachTag({
            variables: {
              attachSubscriptionTagInput: {
                companyTagId: action.option.value,
                subscriptionId,
              },
            },
            onCompleted: () => {
              refetch();
            },
            onError: () => {
              errorToast({ title: i18nSubscriptionDetailsTags('Toasts.AttachError') });
            },
          });
        }
        break;
      }
      case 'pop-value':
      case 'remove-value': {
        if (action.removedValue) {
          detachTag({
            variables: {
              detachSubscriptionTagInput: {
                companyTagId: action.removedValue.value,
                subscriptionId,
              },
            },
            onCompleted: () => {
              refetch();
            },
            onError: () => {
              errorToast({ title: i18nSubscriptionDetailsTags('Toasts.DetachError') });
            },
          });
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  const getNoOptionsMessage = (): string => {
    return i18nSubscriptionDetailsTags('SelectNoOptions');
  };

  /* ** Subscription tags mapped to an option array ** */
  const tags = data?.subscription.tags || [];
  const tagOptions = tags.map((tag) => ({ label: tag.name, value: tag.id, color: tag.color }));

  return (
    <Stack px="4" spacing="3">
      <Text color="gray.600" fontWeight="600">
        Tags
      </Text>
      {loading ? (
        <Skeleton h="60px" />
      ) : (
        <AsyncCreatableSelect
          key={selectKey}
          defaultOptions
          isMulti
          isSearchable
          chakraStyles={chakraStyles}
          isClearable={false}
          isLoading={loading}
          loadOptions={loadOptions}
          menuPlacement="auto"
          noOptionsMessage={getNoOptionsMessage}
          placeholder={i18nSubscriptionDetailsTags('SelectPlaceholder')}
          value={tagOptions}
          components={{
            MultiValue: TagMultiValue,
            Input: TagInput,
          }}
          formatCreateLabel={(inputValue: string): JSX.Element => (
            <TagPlaceholder color={createTagColor} inputValue={inputValue} />
          )}
          formatOptionLabel={(option: TagOption): JSX.Element => (
            <Badge
              bg={!option.color ? 'transparent' : undefined}
              colorScheme={option.color}
              p={!option.color ? '0' : undefined}
            >
              {option.label}
            </Badge>
          )}
          onChange={handleTagChange}
          onCreateOption={handleTagCreate}
        />
      )}
    </Stack>
  );
}

export type SubscriptionDetailsTagsProps = Props;
